import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ApplicationsManager.css';

function ApplicationsManager() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5001/api/forms/freelancer-applications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    const token = localStorage.getItem('token');
    try {
      await axios.patch(
        `http://localhost:5001/api/forms/freelancer-applications/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchApplications();
      if (selectedApp && selectedApp.id === id) {
        setSelectedApp({ ...selectedApp, status });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Σφάλμα κατά την ενημέρωση');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την αίτηση;')) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`http://localhost:5001/api/forms/freelancer-applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Σφάλμα κατά τη διαγραφή');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { className: 'pending', label: 'Εκκρεμεί' },
      approved: { className: 'approved', label: 'Εγκρίθηκε' },
      rejected: { className: 'rejected', label: 'Απορρίφθηκε' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`status-badge ${badge.className}`}>{badge.label}</span>;
  };

  if (loading) {
    return <div className="loading">Φόρτωση αιτήσεων...</div>;
  }

  return (
    <div className="applications-manager">
      <div className="manager-header">
        <h2>Αιτήσεις Freelancers</h2>
        <span className="count-badge">{applications.length} αιτήσεις</span>
      </div>

      {applications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <p>Δεν υπάρχουν αιτήσεις ακόμα</p>
        </div>
      ) : (
        <div className="applications-container">
          <div className="applications-list">
            {applications.map(app => (
              <div 
                key={app.id} 
                className={`application-card ${selectedApp?.id === app.id ? 'selected' : ''}`}
                onClick={() => setSelectedApp(app)}
              >
                <div className="app-header">
                  <h3>{app.name}</h3>
                  {getStatusBadge(app.status)}
                </div>
                <div className="app-info">
                  <p className="app-email">✉️ {app.email}</p>
                  <p className="app-rate">💰 €{app.hourlyRate}/ώρα</p>
                </div>
                <div className="app-skills">
                  {app.skills.slice(0, 3).map((skill, idx) => (
                    <span key={idx} className="skill-tag">{skill}</span>
                  ))}
                  {app.skills.length > 3 && <span className="skill-tag">+{app.skills.length - 3}</span>}
                </div>
                <p className="app-date">
                  📅 {new Date(app.submittedAt).toLocaleDateString('el-GR')}
                </p>
              </div>
            ))}
          </div>

          {selectedApp && (
            <div className="application-details">
              <div className="details-header">
                <h2>{selectedApp.name}</h2>
                <button 
                  className="btn-close"
                  onClick={() => setSelectedApp(null)}
                >
                  ✕
                </button>
              </div>

              <div className="details-content">
                <div className="detail-section">
                  <h3>Στοιχεία Επικοινωνίας</h3>
                  <p><strong>Email:</strong> <a href={`mailto:${selectedApp.email}`}>{selectedApp.email}</a></p>
                  {selectedApp.phone && <p><strong>Τηλέφωνο:</strong> {selectedApp.phone}</p>}
                </div>

                <div className="detail-section">
                  <h3>Επαγγελματικά Στοιχεία</h3>
                  <p><strong>Εμπειρία:</strong> {selectedApp.experience} χρόνια</p>
                  <p><strong>Ωριαία Αμοιβή:</strong> €{selectedApp.hourlyRate}</p>
                  <p><strong>Διαθεσιμότητα:</strong> {selectedApp.availability}</p>
                </div>

                <div className="detail-section">
                  <h3>Δεξιότητες</h3>
                  <div className="skills-list">
                    {selectedApp.skills.map((skill, idx) => (
                      <span key={idx} className="skill-badge">{skill}</span>
                    ))}
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Βιογραφικό</h3>
                  <p className="bio-text">{selectedApp.bio}</p>
                </div>

                {(selectedApp.portfolio || selectedApp.github || selectedApp.linkedin) && (
                  <div className="detail-section">
                    <h3>Links</h3>
                    {selectedApp.portfolio && (
                      <p><strong>Portfolio:</strong> <a href={selectedApp.portfolio} target="_blank" rel="noopener noreferrer">{selectedApp.portfolio}</a></p>
                    )}
                    {selectedApp.github && (
                      <p><strong>GitHub:</strong> <a href={selectedApp.github} target="_blank" rel="noopener noreferrer">{selectedApp.github}</a></p>
                    )}
                    {selectedApp.linkedin && (
                      <p><strong>LinkedIn:</strong> <a href={selectedApp.linkedin} target="_blank" rel="noopener noreferrer">{selectedApp.linkedin}</a></p>
                    )}
                  </div>
                )}

                <div className="detail-section">
                  <h3>Ενέργειες</h3>
                  <div className="action-buttons">
                    <button 
                      className="btn btn-success"
                      onClick={() => handleStatusChange(selectedApp.id, 'approved')}
                      disabled={selectedApp.status === 'approved'}
                    >
                      ✅ Έγκριση
                    </button>
                    <button 
                      className="btn btn-warning"
                      onClick={() => handleStatusChange(selectedApp.id, 'pending')}
                      disabled={selectedApp.status === 'pending'}
                    >
                      ⏸️ Εκκρεμεί
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleStatusChange(selectedApp.id, 'rejected')}
                      disabled={selectedApp.status === 'rejected'}
                    >
                      ❌ Απόρριψη
                    </button>
                    <button 
                      className="btn btn-outline"
                      onClick={() => handleDelete(selectedApp.id)}
                    >
                      🗑️ Διαγραφή
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ApplicationsManager;
